import React, { useRef, useState } from 'react';
import styles from './TcsoInventoryCheckScreen.module.css';
import inputStyles from './TcsoDualFuelMeterDetailsScreen.module.css';
import BackButton from '../common/button/BackButton';
import SmoothProgressBar from '../common/progressBar/SmoothProgressBar';
import {
  TcsoDualFuelScreen,
  useTcsoDualFuelContext,
  useTcsoDualFuelScreenContext,
} from '../../context/TcsoDualFuelScreenContext';
import { useChatReasonContext, useHomePageContext } from '../../context/HomePageContext';
import { CommsCheckReportingRequest, getAuthTokenOrLogOut, getMpxnError, getMsnError, isMpxnValid, isMsnValid } from '../util';
import { isDualFuelHappyPath, runDualFuelCommsCheck, toSingleFuelReportingViews } from '../commsCheck/dualFuelCommsCheck';
import { summariseDualFuelCommsOutcome } from '../commsCheck/dualFuelCommsCheckOutcome';
import { determineCommsCheckResultForReporting } from '../commsCheck/CommsCheck';
import { reportingClient } from '../../api/atomProxyClient';
import TcsoDualFuelResultsScreen from './TcsoDualFuelResultsScreen';
import TcsoDualFuelErrorScreen from './TcsoDualFuelErrorScreen';
import PerformTcso from './perform/PerformTcso';
import { DualFuelCommsCheckResult } from '../../types/comms-check';
import { startChatSession } from '../chat/startChatSession';
import { TCSO_DUAL_FUEL } from '../chat/Chat';
import { BLOCKER_TEXT, CONTACT_SST_FOR_SUPPORT, findSmetsBlocker, TcsoBlocker } from './tcsoBlocker';

interface MeterDetailInputProps {
  label: string;
  inputName: string;
  inputValue: string;
  onChangeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessage: string;
}

const MeterDetailInput: React.FC<MeterDetailInputProps> = ({ label, inputName, inputValue, onChangeHandler, errorMessage }) => {
  return (
    <div className={inputStyles.formContainer}>
      <label htmlFor={inputName}>{label}</label>
      <div className={inputStyles.inputContainer}>
        <input id={inputName} type="text" className={inputStyles.inputField} value={inputValue} onChange={onChangeHandler} />
      </div>
      {errorMessage && <span className="errorMessage">{errorMessage}</span>}
    </div>
  );
};

export default function TcsoDualFuelMeterDetailsScreen() {
  const { chatReason, setChatReason } = useChatReasonContext();
  const { homePageState, setHomePageState } = useHomePageContext();
  const { setTcsoDualFuelScreenInputs } = useTcsoDualFuelScreenContext();
  const { tcsoDualFuelInputs, setTcsoDualFuelInputs } = useTcsoDualFuelContext();

  const [mpanErrorMessage, setMpanErrorMessage] = useState('');
  const [mprnErrorMessage, setMprnErrorMessage] = useState('');
  const [elecMsnErrorMessage, setElecMsnErrorMessage] = useState('');
  const [gasMsnErrorMessage, setGasMsnErrorMessage] = useState('');

  const [commsCheckStarted, setCommsCheckStarted] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [commsCheckResult, setCommsCheckResult] = useState<DualFuelCommsCheckResult | null>(null);
  const [tcsoStarted, setTcsoStarted] = useState(false);
  const journeyIdRef = useRef('');
  const commsCheckStartTimeRef = useRef(new Date(0));

  const goToChecklistScreen = () => {
    setHomePageState({
      ...homePageState,
      automationInProgress: false,
    });
    setTcsoDualFuelScreenInputs({
      tcsoDualFuelScreen: TcsoDualFuelScreen.TCSO_DUAL_FUEL_CHECKLIST_SCREEN,
    });
  };

  const goBack = () => {
    setChatReason('');
    goToChecklistScreen();
  };

  const mpanChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMpanErrorMessage(getMpxnError(event.target.value));
    setTcsoDualFuelInputs({ ...tcsoDualFuelInputs, mpan: event.target.value });
  };

  const mprnChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMprnErrorMessage(getMpxnError(event.target.value));
    setTcsoDualFuelInputs({ ...tcsoDualFuelInputs, mprn: event.target.value });
  };

  const elecMsnChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setElecMsnErrorMessage(getMsnError(event.target.value));
    setTcsoDualFuelInputs({ ...tcsoDualFuelInputs, elecMsn: event.target.value });
  };

  const gasMsnChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGasMsnErrorMessage(getMsnError(event.target.value));
    setTcsoDualFuelInputs({ ...tcsoDualFuelInputs, gasMsn: event.target.value });
  };

  const runCommsCheckHandler = () => {
    const { mpan, mprn, elecMsn, gasMsn } = tcsoDualFuelInputs;

    if (!isMpxnValid(mpan) || !isMpxnValid(mprn) || !isMsnValid(elecMsn) || !isMsnValid(gasMsn)) {
      setMpanErrorMessage(getMpxnError(mpan));
      setMprnErrorMessage(getMpxnError(mprn));
      setElecMsnErrorMessage(getMsnError(elecMsn));
      setGasMsnErrorMessage(getMsnError(gasMsn));
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    journeyIdRef.current = crypto.randomUUID();
    commsCheckStartTimeRef.current = new Date();
    setCommsCheckResult(null);
    setCommsCheckStarted(true);
    void runDualFuelCommsCheck({ mpan, mprn, elecMsn, gasMsn }, controller.signal).then((result) => {
      if (controller.signal.aborted) {
        return;
      }
      setCommsCheckResult(result);
      void reportCommsCheck(result);
    });
  };

  const reportCommsCheck = async (result: DualFuelCommsCheckResult) => {
    const resultTimeStamp = new Date();
    const { userEmail } = await getAuthTokenOrLogOut();
    const { elec, gas } = toSingleFuelReportingViews(result);
    const shared = {
      decommissionJourneyId: journeyIdRef.current,
      contactReason: chatReason,
      engineerEmail: userEmail ?? '',
      commsCheckStartingTimeStamp: commsCheckStartTimeRef.current.toISOString(),
      commsCheckResultTimeStamp: resultTimeStamp.toISOString(),
      commsCheckTimeTaken: resultTimeStamp.getTime() - commsCheckStartTimeRef.current.getTime(),
    };
    const elecRequest: CommsCheckReportingRequest = {
      ...shared,
      mpxn: tcsoDualFuelInputs.mpan,
      msn: tcsoDualFuelInputs.elecMsn,
      commsCheckResultStatus: determineCommsCheckResultForReporting(elec.result, elec.isMeterActive),
    };
    const gasRequest: CommsCheckReportingRequest = {
      ...shared,
      mpxn: tcsoDualFuelInputs.mprn,
      msn: tcsoDualFuelInputs.gasMsn,
      commsCheckResultStatus: determineCommsCheckResultForReporting(gas.result, gas.isMeterActive),
    };
    reportingClient.commsCheck(elecRequest).then();
    reportingClient.commsCheck(gasRequest).then();
  };

  const backFromWaitingHandler = () => {
    abortControllerRef.current?.abort();
    setHomePageState({
      ...homePageState,
      automationInProgress: false,
    });
    setChatReason('');
  };

  const cancelCommsCheckHandler = () => {
    abortControllerRef.current?.abort();
    setCommsCheckStarted(false);
  };

  const backToInputsHandler = () => {
    setCommsCheckResult(null);
    setCommsCheckStarted(false);
  };

  const confirmSiteDetailsHandler = () => {
    setTcsoStarted(true);
  };

  const buildTcsoChatMessage = (result: DualFuelCommsCheckResult, details: { newChfId: string; newGpfId: string }) => {
    return `Hi
We’re here to help. I can see you need assistance to complete the TCSO for:

MPAN:
${tcsoDualFuelInputs.mpan}

MPRN:
${tcsoDualFuelInputs.mprn}

Elec MSN:
${tcsoDualFuelInputs.elecMsn}

Gas MSN:
${tcsoDualFuelInputs.gasMsn}

Address:
${result.xrinv.address}

New CHF GUID:
${details.newChfId}

New GPF GUID:
${details.newGpfId}`;
  };

  const buildChatData = (result: DualFuelCommsCheckResult) => {
    return `Hi
We’re here to help. I can see you need assistance with a Dual Fuel Comms Check for:

MPAN:
${tcsoDualFuelInputs.mpan}

MPRN:
${tcsoDualFuelInputs.mprn}

Elec MSN:
${tcsoDualFuelInputs.elecMsn}

Gas MSN:
${tcsoDualFuelInputs.gasMsn}

Comms Check Result:
${summariseDualFuelCommsOutcome(result)}`;
  };

  const buildBlockedChatMessage = (result: DualFuelCommsCheckResult, blocker: TcsoBlocker) => {
    return `Hi
We’re here to help. I can see you need assistance to complete the TCSO for:

MPAN:
${tcsoDualFuelInputs.mpan}

MPRN:
${tcsoDualFuelInputs.mprn}

Elec MSN:
${tcsoDualFuelInputs.elecMsn}

Gas MSN:
${tcsoDualFuelInputs.gasMsn}

Address:
${result.xrinv.address}

CHF GUID:
${result.xrinv.data.find((device) => device.deviceType === 'CHF')?.deviceId ?? ''}

Reason:
${BLOCKER_TEXT[blocker]}`;
  };

  const startChatHandler = async (message: string) => {
    const outcome = await startChatSession({
      message,
      mpxn: tcsoDualFuelInputs.mpan,
      chatReason: TCSO_DUAL_FUEL,
    });
    if (outcome === 'STARTED') {
      // Leave the automation but keep the chat reason, so the Chat component takes over
      goToChecklistScreen();
    }
  };

  if (commsCheckResult) {
    if (isDualFuelHappyPath(commsCheckResult)) {
      if (tcsoStarted) {
        const result = commsCheckResult;
        return (
          <PerformTcso
            requestBase={{
              identifier: tcsoDualFuelInputs.mpan,
              chfDeviceId: result.xrinv.data.find((device) => device.deviceType === 'CHF')?.deviceId ?? '',
              gpfDeviceId: result.xrinv.data.find((device) => device.deviceType === 'GPF')?.deviceId ?? '',
            }}
            journeyId={journeyIdRef.current}
            chatReason={TCSO_DUAL_FUEL}
            buildChatMessage={(details) => buildTcsoChatMessage(result, details)}
            onFinish={goBack}
            onPrevious={() => setTcsoStarted(false)}
            onBackToStart={() => {
              setTcsoStarted(false);
              backFromWaitingHandler();
            }}
            onChatStarted={goToChecklistScreen}
          />
        );
      }
      const result = commsCheckResult;
      const blocker = findSmetsBlocker(result.xrinv.data);
      return (
        <TcsoDualFuelResultsScreen
          result={result}
          warning={
            blocker
              ? {
                  text: `${BLOCKER_TEXT[blocker]} ${CONTACT_SST_FOR_SUPPORT}`,
                  onStartChat: () => startChatHandler(buildBlockedChatMessage(result, blocker)),
                }
              : undefined
          }
          onConfirm={confirmSiteDetailsHandler}
          onPrevious={backToInputsHandler}
          onBackToStart={backFromWaitingHandler}
        />
      );
    }
    const result = commsCheckResult;
    return (
      <TcsoDualFuelErrorScreen
        result={result}
        onTryAgain={runCommsCheckHandler}
        onStartChat={() => startChatHandler(buildChatData(result))}
        onPrevious={backToInputsHandler}
        onBackToStart={backFromWaitingHandler}
      />
    );
  }

  if (commsCheckStarted) {
    return (
      <div className={`commsCheckDecommFlexContainer ${inputStyles.screenContainer}`}>
        {/* Worst case in prd/uat: sequential XRINV + XRDLG at 220s each (10s initial + 7 x 30s polls) = 7 min 20 s */}
        <SmoothProgressBar durationMinutes={440 / 60} />
        <div className={'commsCheckDecommContent'}>
          <div className={'m-t-8'}>
            <BackButton onClick={backFromWaitingHandler} />
            <div className={styles.waitingScreen}>
              <div className={'bodyTitleText'}>Running Comms Check</div>
              <div className={'bodyText'}>This may take around 7 min, please wait.</div>
            </div>
          </div>
        </div>
        <div className={`tcsoFooter ${inputStyles.footer}`}>
          <button className={`${styles.tcsoTimeoutButton} ${styles.bgcGreen}`} onClick={cancelCommsCheckHandler}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`commsCheckDecommFlexContainer ${inputStyles.screenContainer}`}>
      <div className={'commsCheckDecommContent'}>
        <div className={'m-t-8'}>
          <BackButton onClick={goBack} />
          <div className={'bodyTitleText m-t-22'}>Comms Check Details</div>
          <div className={'bodyText'}>Enter the site details to perform a Comms Check for Elec and Gas meters</div>

          <MeterDetailInput
            label={'Enter MPAN of this address'}
            inputName={'mpanInput'}
            inputValue={tcsoDualFuelInputs.mpan}
            onChangeHandler={mpanChangeHandler}
            errorMessage={mpanErrorMessage}
          />

          <MeterDetailInput
            label={'Enter MPRN of this address'}
            inputName={'mprnInput'}
            inputValue={tcsoDualFuelInputs.mprn}
            onChangeHandler={mprnChangeHandler}
            errorMessage={mprnErrorMessage}
          />

          <MeterDetailInput
            label={'Enter the Electric MSN'}
            inputName={'elecMsnInput'}
            inputValue={tcsoDualFuelInputs.elecMsn}
            onChangeHandler={elecMsnChangeHandler}
            errorMessage={elecMsnErrorMessage}
          />

          <MeterDetailInput
            label={'Enter the Gas MSN'}
            inputName={'gasMsnInput'}
            inputValue={tcsoDualFuelInputs.gasMsn}
            onChangeHandler={gasMsnChangeHandler}
            errorMessage={gasMsnErrorMessage}
          />
        </div>
      </div>

      <div className={`tcsoFooter ${inputStyles.footer}`}>
        <button className={`${styles.tcsoTimeoutButton} ${styles.bgcGreen} ${styles.mGB8}`} onClick={runCommsCheckHandler}>
          Run Comms Check
        </button>
        <button className={`${styles.tcsoTimeoutButton} ${styles.bgcDefault}`} onClick={goToChecklistScreen}>
          Previous
        </button>
      </div>
    </div>
  );
}
